import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ProjectService } from '../../../services/project.service';
import { ParticipantService } from '../../../services/participant.service';
import { CompanyService } from '../../../services/company.service';
import { ReportJobsService } from '../../../services/report-jobs.service';
import { ReportHubService } from '../../../services/report-hub.service';
import { ToastService } from '../../../services/toast.service';

interface DropdownOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-project-enrollments-reports',
  templateUrl: './project-enrollments-reports.component.html',
  styleUrls: ['./project-enrollments-reports.component.scss']
})
export class ProjectEnrollmentsReportsComponent implements OnInit, OnDestroy {
  reportForm!: FormGroup;
  loading = false;
  projectsDropdown: DropdownOption[] = [];
  participantsDropdown: DropdownOption[] = [];
  companiesDropdown: DropdownOption[] = [];

  private reportHubSub!: Subscription;
  private formSubs = new Subscription();
  private pendingJobId: string | null = null;

  months = [
    { label: 'Enero', value: 'ENE' },
    { label: 'Febrero', value: 'FEB' },
    { label: 'Marzo', value: 'MAR' },
    { label: 'Abril', value: 'ABR' },
    { label: 'Mayo', value: 'MAY' },
    { label: 'Junio', value: 'JUN' },
    { label: 'Julio', value: 'JUL' },
    { label: 'Agosto', value: 'AGO' },
    { label: 'Septiembre', value: 'SEP' },
    { label: 'Octubre', value: 'OCT' },
    { label: 'Noviembre', value: 'NOV' },
    { label: 'Diciembre', value: 'DIC' },
  ];

  constructor(
    private fb: FormBuilder,
    private _projectService: ProjectService,
    private _participantsService: ParticipantService,
    private _companyService: CompanyService,
    private _reportJobsService: ReportJobsService,
    private _reportHubService: ReportHubService,
    private _toastService: ToastService,
  ) { }

  ngOnInit(): void {
    this.reportForm = this.fb.group({
      projectId: [null],
      period: [null],
      participantId: [null],
      companyId: [null]
    });

    this.reportHubSub = this._reportHubService.reportStatusChanged$.subscribe(notification => {
      if (notification.jobId !== this.pendingJobId) return;

      if (notification.status === 'Completed') {
        this.loading = false;
        this._toastService.showToast('success', 'Reporte de inscripciones generado exitosamente. Descargando...');
        this._reportJobsService.downloadReport(notification.jobId, notification.fileName || undefined);
        this.pendingJobId = null;
      } else if (notification.status === 'Failed') {
        this.loading = false;
        this._toastService.showToast('error', `Error al generar el reporte: ${notification.errorMessage || 'Desconocido'}`);
        this.pendingJobId = null;
      } else if (notification.status === 'Processing') {
        this._toastService.showToast('info', 'Procesando reporte de inscripciones...');
      }
    });

    // Lógica de exclusión mutua entre projectId y period
    this.formSubs.add(this.reportForm.get('period')?.valueChanges.subscribe(period => {
      const projectIdControl = this.reportForm.get('projectId');
      if (period) {
        // Si se selecciona período, limpiar y deshabilitar proyecto
        projectIdControl?.setValue(null, { emitEvent: false });
        projectIdControl?.disable({ emitEvent: false });
      } else {
        // Si se limpia período, habilitar proyecto
        projectIdControl?.enable({ emitEvent: false });
      }
    }));

    this.formSubs.add(this.reportForm.get('projectId')?.valueChanges.subscribe(projectId => {
      const periodControl = this.reportForm.get('period');
      if (projectId) {
        // Si se selecciona proyecto, limpiar y deshabilitar período
        periodControl?.setValue(null, { emitEvent: false });
        periodControl?.disable({ emitEvent: false });
      } else {
        // Si se limpia proyecto, habilitar período
        periodControl?.enable({ emitEvent: false });
      }
    }));

    this.loadProjects();
    this.loadParticipants();
    this.loadCompanies();
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  private loadProjects(): void {
    this._projectService.getLookup().subscribe({
      next: (resp) => {
        const data = this.extractData(resp);
        this.projectsDropdown = data.map((p: any) => ({
          label: `${p.projectId} - ${p.name}`,
          value: p.id
        }));
      },
      error: (err) => {
        console.error('Error al cargar proyectos:', err);
        this.projectsDropdown = [];
        this._toastService.showToast('error', 'Error al cargar proyectos.');
      }
    });
  }

  private loadParticipants(): void {
    this._participantsService.getAll().subscribe({
      next: (resp) => {
        const data = this.extractData(resp);
        this.participantsDropdown = data.map((p: any) => ({
          label: `${p.firstName} ${p.lastName} - ${p.typeDocument || 'N/A'}: ${p.documentId || 'N/A'}`.trim(),
          value: p.id
        }));
      },
      error: (err) => {
        console.error('Error al cargar participantes:', err);
        this.participantsDropdown = [];
        this._toastService.showToast('error', 'Error al cargar participantes.');
      }
    });
  }

  private loadCompanies(): void {
    this._companyService.getAll().subscribe({
      next: (resp) => {
        const data = this.extractData(resp);
        this.companiesDropdown = data.map((c: any) => ({
          label: `${c.name} - ${c.typeDocument || 'N/A'}: ${c.documentId || 'N/A'}`,
          value: c.id
        }));
      },
      error: (err) => {
        console.error('Error al cargar empresas:', err);
        this.companiesDropdown = [];
        this._toastService.showToast('error', 'Error al cargar empresas.');
      }
    });
  }

  // 🔍 Búsqueda server-side para proyectos
  onSearchProjects(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this.loadProjects();
      return;
    }
    this._projectService.getLookup(query).subscribe({
      next: (resp) => {
        const data = this.extractData(resp);
        this.projectsDropdown = data.map((p: any) => ({
          label: `${p.projectId} - ${p.name}`,
          value: p.id
        }));
      },
      error: (err) => {
        console.error('Error al buscar proyectos:', err);
        this.projectsDropdown = [];
        this._toastService.showToast('error', 'Error al cargar proyectos.');
      }
    });
  }

  // 🔍 Búsqueda server-side para participantes
  onSearchParticipants(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this.loadParticipants();
      return;
    }
    this._participantsService.getAllFiltered(query).subscribe({
      next: (resp) => {
        const data = this.extractData(resp);
        this.participantsDropdown = data.map((p: any) => ({
          label: `${p.firstName} ${p.lastName} - ${p.typeDocument || 'N/A'}: ${p.documentId || 'N/A'}`.trim(),
          value: p.id
        }));
      },
      error: (err) => {
        console.error('Error al buscar participantes:', err);
        this.participantsDropdown = [];
        this._toastService.showToast('error', 'Error al cargar participantes.');
      }
    });
  }

  // 🔍 Búsqueda server-side para empresas
  onSearchCompanies(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this.loadCompanies();
      return;
    }
    this._companyService.getAllFiltered(query).subscribe({
      next: (resp) => {
        const data = this.extractData(resp);
        this.companiesDropdown = data.map((c: any) => ({
          label: `${c.name} - ${c.typeDocument || 'N/A'}: ${c.documentId || 'N/A'}`,
          value: c.id
        }));
      },
      error: (err) => {
        console.error('Error al buscar empresas:', err);
        this.companiesDropdown = [];
        this._toastService.showToast('error', 'Error al cargar empresas.');
      }
    });
  }

  downloadExcelReport(): void {
    if (this.reportForm.invalid) {
      this.reportForm.markAllAsTouched();
      return;
    }
    const raw = this.reportForm.getRawValue() as { projectId?: number | null, period?: string | null, participantId?: number | null, companyId?: number | null };
    this.loading = true;
    this._reportJobsService.queueProjectEnrollmentsReport({
      projectId: raw.projectId ?? null,
      participantId: raw.participantId ?? null,
      companyId: raw.companyId ?? null,
      period: raw.period ?? null
    }).subscribe({
      next: (response) => {
        this.pendingJobId = response.jobId;
        this._toastService.showToast('info', 'Generando reporte de inscripciones en segundo plano...');
      },
      error: (error: any) => {
        console.error('Error al solicitar reporte:', error);
        this.loading = false;
        this._toastService.showToast('error', 'Error al solicitar el reporte');
      }
    });
  }

  ngOnDestroy(): void {
    this.reportHubSub?.unsubscribe();
    this.formSubs.unsubscribe();
  }
}


